<template>
    <vx-dropdown
        ref="dropdown"
        justify="center"
        align="bottom"
        menu-class="date-range-dropdown-menu"
    >
        <template v-slot="{ visible }">
            <slot :visible="visible" :value="localValue">
                <vx-action-icon
                    :icon="!visible || !hasRange ? 'CalendarIcon' : 'XIcon'"
                    :hover-icon="!hasRange ? 'CalendarIcon' : 'XIcon'"
                    :hover-color="!hasRange ? 'primary' : 'danger'"
                    :active-color="!visible || !hasRange ? 'primary' : 'danger'"
                    :active="value && !!value.start"
                    @click="tryClear($event, visible)"
                />
            </slot>
        </template>
        <template v-slot:menu>
            <div class="p-2">
                <slot name="top"></slot>
                <div class="flex justify-between">
                    <slot name="left"> </slot>

                    <date-picker
                        ref="calendar"
                        v-model="localValue"
                        :attributes="attributes_x"
                        :disabled-dates="disabledDates"
                        :available-dates="availableDates"
                        :is-dark="isThemeDark"
                        is-range
                    />
                    <slot name="right">
                        <div v-if="showPresets_x" class="flex flex-col ml-2 preset-range-section">
                            <vx-button
                                v-for="button in rangeButtons"
                                type="flat"
                                class="preset-button whitespace-nowrap text-left pl-2 ml-1"
                                :class="{ active: checkRangeSelected(button.range) }"
                                :key="stringifyRange(button.range)"
                                @click="localValue = button.range"
                            >
                                {{ button.title }}
                            </vx-button>
                        </div>
                    </slot>
                </div>
                <slot name="bottom">
                    <div class="w-full flex items-center justify-center mt-3 pt-2 bottom-section">
                        <template v-if="hasRange">
                            <h5>{{ startDateString }}</h5>
                            <vx-basic-icon icon="ArrowRightIcon" size="7" class="mx-3" />
                            <h5>{{ endDateString }}</h5>
                        </template>
                        <h5 v-else>No Range Selected</h5>
                    </div>
                </slot>
            </div>
        </template>
    </vx-dropdown>
</template>

<script>
import { mapGetters } from 'vuex'
import { DatePicker } from 'v-calendar'

export default {
    name: 'vx-date-range-dropdown',
    components: {
        DatePicker
    },
    props: {
        value: {
            type: Object
        },
        attributes: {
            type: Array,
            default: () => []
        },
        disabledDates: {
            type: [Object, Array]
        },
        availableDates: {
            type: [Object, Array]
        },
        closeOnSelect: {
            type: Boolean
        },
        showPresets: {
            type: Boolean
        },
        customPresets: {
            type: Array
        }
    },
    watch: {
        startDate(newV) {
            if (!newV || !this.$refs.calendar) {
                return
            }
            this.$refs.calendar.move(newV)
        }
    },
    data() {
        return {
            now: new Date().getStartOfDay()
        }
    },
    computed: {
        ...mapGetters({
            isThemeDark: 'user/isThemeDark'
        }),
        hasRange() {
            return this.startDate || this.endDate
        },
        showPresets_x() {
            return this.showPresets || (this.customPresets && this.customPresets.any())
        },
        localValue: {
            get() {
                // Either use value or start/end NEVER both
                // checking value so that we don't produce { start: null, end: null }
                if (!this.value) {
                    return null
                }

                return {
                    start: this.startDate,
                    end: this.endDate
                }
            },
            set(val) {
                if (val && val.end) {
                    val.end = val.end.getEndOfDay()
                }
                this.$emit('input', val)

                if (!this.closeOnSelect) {
                    return
                }
                setTimeout(this.$refs.dropdown.close, 200)
            }
        },
        startDate() {
            if (!this.value || !this.value.start) {
                return null
            }
            return new Date(this.value.start).getStartOfDay()
        },
        endDate() {
            if (!this.value || !this.value.end) {
                return null
            }
            return new Date(this.value.end).getEndOfDay()
        },
        attributes_x() {
            return [
                ...this.attributes,
                {
                    key: 'today',
                    bar: 'red',
                    dates: this.now
                }
            ]
        },
        rangeButtons() {
            if (this.customPresets) {
                return this.customPresets.map(({ title, range }) => {
                    return {
                        title,
                        range: {
                            start: range.start && new Date(range.start).getStartOfDay(),
                            end: range.end && new Date(range.end).getEndOfDay()
                        }
                    }
                })
            }
            return [
                {
                    title: 'Today',
                    range: this.today
                },
                {
                    title: 'Yesterday',
                    range: this.yesterday
                },
                {
                    title: 'This Week',
                    range: this.thisWeek
                },
                {
                    title: 'This Month',
                    range: this.thisMonth
                },
                {
                    title: 'Last Month',
                    range: this.lastMonth
                },
                {
                    title: this.getFiscalYearTitle(this.thisFinancialYear),
                    range: this.thisFinancialYear
                },
                {
                    title: this.getFiscalYearTitle(this.lastFinancialYear),
                    range: this.lastFinancialYear
                }
            ]
        },
        startDateString() {
            if (!this.startDate) {
                return 'Dawn of Time'
            }
            return new Date(this.startDate).toLocaleDateString('en-AU')
        },
        endDateString() {
            if (!this.endDate) {
                return 'End of Time'
            }
            return new Date(this.endDate).toLocaleDateString('en-AU')
        },
        today() {
            return this.now.getDayRange()
        },
        yesterday() {
            return this.now.addDays(-1).getDayRange()
        },
        thisWeek() {
            return this.now.getWorkWeekRange()
        },
        thisMonth() {
            return this.now.getMonthRange()
        },
        lastMonth() {
            return this.now.addMonths(-1).getMonthRange()
        },
        thisFinancialYear() {
            return this.now.getFiscalYearRange()
        },
        lastFinancialYear() {
            return this.now.addYears(-1).getFiscalYearRange()
        }
    },
    methods: {
        getFiscalYearTitle(range) {
            var startYear = range.start.getFullYear()
            var endYear = range.end.getFullYear().toString().substr(2)
            return `FY ${startYear}-${endYear}`
        },
        stringifyRange(range) {
            if (!range) {
                return ''
            }
            return `${range.start && range.start.getTime()}_${range.end && range.end.getTime()}`
        },
        checkRangeSelected(range) {
            return this.stringifyRange(range) == this.stringifyRange(this.localValue)
        },
        tryClear(e, visible) {
            if (!visible) {
                return
            }
            e.preventDefault()
            this.localValue = null
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';
.preset-button {
    color: black;
}
.theme-dark {
    .preset-button {
        color: #fefefe !important;
    }
}
.preset-button.active {
    background: rgba(var(--vs-primary), 0.8) !important;
    color: rgba(var(--vs-dark-grey-2), 1) !important;
}

.bottom-section {
    border-top: 1px solid rgba(var(--vs-dark), 1);
}

.preset-range-section {
    border-left: 1px solid rgba(var(--vs-dark), 1);
    padding-left: 0.5rem;
}
</style>

<style lang="scss">
.date-range-dropdown-menu {
    .vc-container {
        background-color: transparent;
        border: none !important;
    }
}
</style>
